import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Badge } from '@shadcn/components/ui/badge';
import { Progress } from '@shadcn/components/ui/progress';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  Package
} from 'lucide-react';


import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import { 
  TICKET_TYPE_LABELS, 
  META_STATUS_LABELS,
  CHART_COLORS,
  COMPONENT_THEMES,
  THEME_CLASSES
} from '../../constants';


const DashboardStats = ({
  statistics,
  onMetaStatusClick,
  activeMetaStatus
}) => {


  if (!statistics) {
    return null;
  }

  const {
    totalTickets,
    ticketsByType = {},
    ticketsByMetaStatus = {},
    recentActivity = {}
  } = statistics;

  const {
    newTicketsToday = 0,
    completedTicketsToday = 0,
    pendingTickets = 0
  } = recentActivity;

  /**
   * Calculate percentage
   */
  const calculatePercentage = (value, total) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  /**
   * Get trend indicator
   */
  const getTrendIndicator = (current, previous) => {
    if (!previous || previous === 0) return null;
    
    const change = ((current - previous) / previous) * 100;
    const isPositive = change > 0;
    
    return (
      <div className={`flex items-center gap-1 text-xs ${
        isPositive ? THEME_CLASSES.TEXT_SECONDARY : THEME_CLASSES.TEXT_MUTED
      }`}>
        {isPositive ? (
          <TrendingUp className="w-3 h-3" />
        ) : (
          <TrendingDown className="w-3 h-3" />
        )}
        {Math.abs(change).toFixed(1)}%
      </div>
    );
  };

  return (
    <div className="grid gap-4 sm:gap-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Tickets */}
        <Card className={COMPONENT_THEMES.card.base}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${THEME_CLASSES.TEXT_MUTED}`}>Total Tickets</p>
                <p className={`text-2xl font-bold ${THEME_CLASSES.TEXT_PRIMARY}`}>{totalTickets.toLocaleString()}</p>
              </div>
              <Package className={`w-8 h-8 ${THEME_CLASSES.TEXT_SECONDARY}`} />
            </div>
          </CardContent>
        </Card>

        {/* New Today */}
        <Card className={COMPONENT_THEMES.card.base}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${THEME_CLASSES.TEXT_MUTED}`}>New Today</p>
                <p className={`text-2xl font-bold ${THEME_CLASSES.TEXT_PRIMARY}`}>{newTicketsToday}</p>
              </div>
              <AlertCircle className={`w-8 h-8 ${THEME_CLASSES.TEXT_SECONDARY}`} />
            </div>
          </CardContent>
        </Card>

        {/* Completed Today */}
        <Card className={COMPONENT_THEMES.card.base}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${THEME_CLASSES.TEXT_MUTED}`}>Completed Today</p>
                <p className={`text-2xl font-bold ${THEME_CLASSES.TEXT_PRIMARY}`}>{completedTicketsToday}</p>
              </div>
              <CheckCircle className={`w-8 h-8 ${THEME_CLASSES.TEXT_SECONDARY}`} />
            </div>
          </CardContent>
        </Card>

        {/* Pending */}
        <Card className={COMPONENT_THEMES.card.base}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${THEME_CLASSES.TEXT_MUTED}`}>Pending</p>
                <p className={`text-2xl font-bold ${THEME_CLASSES.TEXT_PRIMARY}`}>{pendingTickets}</p>
              </div>
              <Clock className={`w-8 h-8 ${THEME_CLASSES.TEXT_SECONDARY}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Tickets by Status */}
        <Card className={COMPONENT_THEMES.card.base}>
          <CardHeader className={COMPONENT_THEMES.card.header}>
            <CardTitle className={`flex items-center gap-2 ${THEME_CLASSES.TEXT_PRIMARY}`}>
              <BarChart3 className="w-5 h-5" />
              Tickets by Status
            </CardTitle>
            <CardDescription className={THEME_CLASSES.TEXT_MUTED}>
              Click on a status to filter tickets
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(ticketsByMetaStatus).map(([status, count]) => {
              const percentage = calculatePercentage(count, totalTickets);
              const isActive = activeMetaStatus === status;
              
              return (
                <div 
                  key={status} 
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    isActive 
                      ? `${THEME_CLASSES.BORDER_FOCUS} ${THEME_CLASSES.BG_SECONDARY}` 
                      : `${THEME_CLASSES.BORDER_SECONDARY} ${THEME_CLASSES.HOVER_LIGHT} ${THEME_CLASSES.BORDER_HOVER}`
                  }`}
                  onClick={() => onMetaStatusClick?.(status)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <StatusBadge 
                        metaStatus={status}
                        showOriginal={false}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={isActive ? "default" : "secondary"}>
                        {count}
                      </Badge>
                      <span className={`text-sm ${THEME_CLASSES.TEXT_MUTED}`}>
                        {percentage}%
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={percentage} 
                    className="h-2"
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Tickets by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Tickets by Type
            </CardTitle>
            <CardDescription>
              Distribution across different ticket types
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(ticketsByType).map(([type, count], index) => {
              const percentage = calculatePercentage(count, totalTickets);
              const colors = [
                CHART_COLORS.PRIMARY,
                CHART_COLORS.SUCCESS,
                CHART_COLORS.WARNING,
                CHART_COLORS.DANGER
              ];
              const color = colors[index % colors.length];
              
              return (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TicketTypeIcon 
                        type={type} 
                        size="sm" 
                        showBackground={false}
                      />
                      <span className="font-medium">
                        {TICKET_TYPE_LABELS[type] || type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{count}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {percentage}%
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={percentage} 
                    className="h-2"
                    style={{ '--progress-background': color }}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Activity Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Today's Activity
          </CardTitle>
          <CardDescription>
            Summary of ticket activity for today
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-700 mb-1">
                {newTicketsToday}
              </div>
              <div className="text-sm text-green-600 font-medium">
                New Tickets
              </div>
              <div className="text-xs text-green-500 mt-1">
                Created today
              </div>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-700 mb-1">
                {completedTicketsToday}
              </div>
              <div className="text-sm text-blue-600 font-medium">
                Completed
              </div>
              <div className="text-xs text-blue-500 mt-1">
                Resolved today
              </div>
            </div>

            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-2xl font-bold text-orange-700 mb-1">
                {pendingTickets}
              </div>
              <div className="text-sm text-orange-600 font-medium">
                Pending
              </div>
              <div className="text-xs text-orange-500 mt-1">
                Awaiting action
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStats;
